import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Anchor, Plus, Ship, MapPin, Clock, ShieldCheck, 
  ChevronRight, LogOut, AlertTriangle, CheckCircle2, Activity,
  Bell, BellOff, Settings, Sparkles, Shield
} from 'lucide-react';
import WeatherWidget, { useUserLocation } from '../components/WeatherWidget';
import TripRecommendations from '../components/TripRecommendations';
import { 
  isPushSupported, 
  isSubscribedToPush, 
  subscribeToPush, 
  unsubscribeFromPush,
  sendTestNotification
} from '../utils/pushNotifications';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [vessels, setVessels] = useState([]);
  const [recentTrips, setRecentTrips] = useState([]);
  const [equipmentSummary, setEquipmentSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const { location: userLocation } = useUserLocation();

  useEffect(() => {
    fetchData();
    checkNotificationStatus();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, vesselsRes, tripsRes, equipRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/vessels`, { withCredentials: true }),
        axios.get(`${API}/trips`, { withCredentials: true }),
        axios.get(`${API}/equipment/summary`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setVessels(vesselsRes.data);
      setRecentTrips(tripsRes.data.slice(0, 5));
      setEquipmentSummary(equipRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkNotificationStatus = async () => {
    if (isPushSupported()) {
      const subscribed = await isSubscribedToPush();
      setNotificationsEnabled(subscribed);
    }
  };

  const toggleNotifications = async () => {
    setNotificationsLoading(true);
    try {
      if (notificationsEnabled) {
        await unsubscribeFromPush();
        setNotificationsEnabled(false);
        toast.success('Notifications disabled');
      } else {
        await subscribeToPush();
        setNotificationsEnabled(true);
        toast.success('Notifications enabled! You\'ll receive safety reminders.');
        // Send test notification
        setTimeout(() => {
          sendTestNotification().catch(() => {});
        }, 1000);
      }
    } catch (err) {
      console.error('Notification toggle error:', err);
      toast.error(err.message || 'Failed to update notification settings');
    } finally {
      setNotificationsLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleEndTrip = async (tripId) => {
    try {
      await axios.put(`${API}/trips/${tripId}/end`, {}, { withCredentials: true });
      fetchData();
    } catch (err) {
      console.error('Failed to end trip:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#2B3A67] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F4F8] pb-24 page-enter">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b-2 border-[#E2E8F0] px-6 py-4">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#2B3A67] rounded-xl flex items-center justify-center">
              <Anchor className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-[#4A5568]">Welcome back,</p>
              <p className="font-bold text-[#2B3A67]">{user?.name || 'Captain'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isPushSupported() && (
              <Button
                variant="ghost"
                size="icon"
                data-testid="notifications-toggle-btn"
                onClick={toggleNotifications}
                disabled={notificationsLoading}
                className={notificationsEnabled ? 'text-[#3CB371]' : 'text-[#4A5568]'}
              >
                {notificationsEnabled ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              data-testid="logout-btn"
              onClick={handleLogout}
              className="text-[#4A5568] hover:text-[#D32F2F] hover:bg-[#FAEAEA]"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="px-6 py-6 max-w-lg mx-auto space-y-6">
        {/* Weather Widget */}
        {userLocation && (
          <WeatherWidget 
            latitude={userLocation.latitude} 
            longitude={userLocation.longitude} 
            compact={true}
          />
        )}

        {/* Trip Recommendations */}
        {userLocation && !stats?.active_trip && (
          <TripRecommendations
            latitude={userLocation.latitude}
            longitude={userLocation.longitude}
            onSelectWindow={(window) => {
              toast.success(`Great choice! ${window.day} ${window.time_range}`);
              navigate('/vessel-setup');
            }}
          />
        )}

        {/* Active Trip Alert */}
        {stats?.active_trip && (
          <Card className="border-2 border-[#3CB371] bg-[#E8F6ED] shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#3CB371] rounded-xl flex items-center justify-center">
                    <Activity className="w-6 h-6 text-white animate-pulse" />
                  </div>
                  <div>
                    <p className="font-bold text-[#2A8A53]">Active Trip</p>
                    <p className="text-sm text-[#2A8A53]">{stats.active_trip.vessel_name}</p>
                  </div>
                </div>
                <Button
                  data-testid="end-trip-btn"
                  onClick={() => handleEndTrip(stats.active_trip.trip_id)}
                  className="bg-[#2B3A67] hover:bg-[#1A264A] text-white font-bold rounded-xl h-12 px-4"
                >
                  End Trip
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="border-2 border-[#E2E8F0]">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-black text-[#2B3A67]">{stats?.total_trips || 0}</p>
              <p className="text-sm text-[#4A5568] font-medium">Total Trips</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-[#E2E8F0]">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-black text-[#3CB371]">{stats?.average_compliance || 0}%</p>
              <p className="text-sm text-[#4A5568] font-medium">Avg Compliance</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-[#E2E8F0]">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-black text-[#2B3A67]">{stats?.verified_trips || 0}</p>
              <p className="text-sm text-[#4A5568] font-medium">Verified Checks</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-[#E2E8F0]">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-black text-[#2B3A67]">{vessels.length}</p>
              <p className="text-sm text-[#4A5568] font-medium">My Vessels</p>
            </CardContent>
          </Card>
        </div>

        {/* Equipment Expiry Alert */}
        {equipmentSummary && (equipmentSummary.expired > 0 || equipmentSummary.expiring_soon > 0) && (
          <Card
            data-testid="equipment-expiry-alert"
            className={`border-2 cursor-pointer transition-colors ${
              equipmentSummary.expired > 0 ? 'border-[#D32F2F] bg-[#FAEAEA] hover:border-[#B71C1C]' : 'border-[#FF6B35] bg-[#FFF0EA] hover:border-[#E65100]'
            }`}
            onClick={() => navigate('/equipment')}
          >
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  equipmentSummary.expired > 0 ? 'bg-[#D32F2F]' : 'bg-[#FF6B35]'
                }`}>
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className={`font-bold ${equipmentSummary.expired > 0 ? 'text-[#D32F2F]' : 'text-[#FF6B35]'}`}>
                    {equipmentSummary.expired > 0
                      ? `${equipmentSummary.expired} Equipment Expired`
                      : `${equipmentSummary.expiring_soon} Expiring Soon`}
                  </p>
                  <p className="text-xs text-[#4A5568]">Tap to view details</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-[#4A5568]" />
            </CardContent>
          </Card>
        )}

        {/* Start Safety Check CTA */}
        <Button
          data-testid="start-safety-check-btn"
          onClick={() => navigate('/vessel-setup')}
          className="w-full h-16 bg-[#3CB371] hover:bg-[#2A8A53] text-white font-bold text-lg rounded-2xl transition-all active:scale-[0.98] shadow-lg"
        >
          <ShieldCheck className="w-6 h-6 mr-3" />
          Start Safety Check
        </Button>

        {/* My Vessels */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-[#2B3A67] font-['Outfit']">My Vessels</h2>
            <Button
              variant="ghost"
              data-testid="add-vessel-btn"
              onClick={() => navigate('/vessel-setup')}
              className="text-[#3CB371] font-bold hover:bg-[#E8F6ED]"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
          
          {vessels.length === 0 ? (
            <Card className="border-2 border-dashed border-[#E2E8F0]">
              <CardContent className="p-6 text-center">
                <Ship className="w-12 h-12 mx-auto text-[#4A5568] mb-3" />
                <p className="text-[#4A5568] font-medium">No vessels yet</p>
                <p className="text-sm text-[#4A5568]">Add your first vessel to get started</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {vessels.map((vessel) => (
                <Card
                  key={vessel.vessel_id}
                  data-testid={`vessel-card-${vessel.vessel_id}`}
                  className="border-2 border-[#E2E8F0] hover:border-[#2B3A67] transition-colors cursor-pointer"
                  onClick={() => navigate(`/vessel-setup?vessel=${vessel.vessel_id}`)}
                >
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#F0F4F8] rounded-xl flex items-center justify-center">
                        <Ship className="w-6 h-6 text-[#2B3A67]" />
                      </div>
                      <div>
                        <p className="font-bold text-[#2B3A67]">{vessel.name}</p>
                        <p className="text-sm text-[#4A5568]">
                          Class {vessel.vessel_class} • {vessel.length}ft • {vessel.engine_type}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-[#4A5568]" />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Recent Trips */}
        {recentTrips.length > 0 && (
          <div>
            <h2 className="text-xl font-bold text-[#2B3A67] font-['Outfit'] mb-4">Recent Trips</h2>
            <div className="space-y-3">
              {recentTrips.map((trip) => (
                <Card
                  key={trip.trip_id}
                  data-testid={`trip-card-${trip.trip_id}`}
                  className="border-2 border-[#E2E8F0]"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-bold text-[#2B3A67]">{trip.vessel_name}</p>
                          {trip.is_verified && (
                            <span className="inline-flex items-center gap-1 bg-[#E8F6ED] text-[#2A8A53] px-2 py-0.5 rounded-full text-xs font-bold">
                              <ShieldCheck className="w-3 h-3" />
                              Verified
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[#4A5568]">
                          {new Date(trip.start_time).toLocaleDateString()} • {trip.duration_minutes ? `${trip.duration_minutes} min` : 'In Progress'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`text-2xl font-black ${trip.compliance_score === 100 ? 'text-[#3CB371]' : trip.compliance_score >= 70 ? 'text-[#E5A000]' : 'text-[#D32F2F]'}`}>
                          {trip.compliance_score}%
                        </p>
                        <p className="text-xs text-[#4A5568]">
                          {trip.status === 'active' ? (
                            <span className="text-[#3CB371] font-bold">ACTIVE</span>
                          ) : trip.is_compliant ? 'Compliant' : 'Not Compliant'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-[#E2E8F0] px-6 py-4">
        <div className="flex justify-around max-w-lg mx-auto">
          <button className="flex flex-col items-center text-[#2B3A67]">
            <Anchor className="w-6 h-6" />
            <span className="text-xs font-bold mt-1">Home</span>
          </button>
          <button 
            onClick={() => navigate('/vessel-setup')} 
            className="flex flex-col items-center text-[#4A5568]"
          >
            <Ship className="w-6 h-6" />
            <span className="text-xs font-medium mt-1">Vessels</span>
          </button>
          <button 
            data-testid="nav-equipment-btn"
            onClick={() => navigate('/equipment')} 
            className="flex flex-col items-center text-[#4A5568] relative"
          >
            <Shield className="w-6 h-6" />
            <span className="text-xs font-medium mt-1">Equipment</span>
            {equipmentSummary && (equipmentSummary.expired > 0 || equipmentSummary.expiring_soon > 0) && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#D32F2F] rounded-full text-white text-[8px] font-bold flex items-center justify-center">
                {equipmentSummary.expired + equipmentSummary.expiring_soon}
              </span>
            )}
          </button>
          <button className="flex flex-col items-center text-[#4A5568]">
            <Clock className="w-6 h-6" />
            <span className="text-xs font-medium mt-1">History</span>
          </button>
        </div>
      </nav>
    </div>
  );
}
